import { Interval } from 'luxon';
import React, { useMemo } from 'react';

import { CustomerType } from 'apis/merchant-gateway/insights';
import {
  CustomerBaseTotalsInsight,
  CustomersByCustomerTypeInsight,
  CustomersByOrderTypeInsight,
} from 'apis/merchant-gateway/insights/customers/types';
import { ChartData, LegendChartType, Legends, VerticalBarChart } from 'modules/experience-core';
import {
  useCustomerBaseTotalsInsight,
  useCustomersByCustomerTypeInsight,
  useCustomersByOrderTypeInsight,
} from 'modules/insights-v2/hooks/useInsightApi';

import { InsightEntity, OrderType } from '../../types';
import ErrorView from '../ErrorView';
import LoadingView from '../LoadingView';

type CustomerBaseChartProps = {
  insightEntity: InsightEntity;
  dateInterval: Interval;
  selectedChartType: 'TOTAL' | 'NEW_VS_RETURNING' | 'REGULAR_VS_WOLT_PLUS';
  selectedLegends: Legends<'total'> | Legends<CustomerType> | Legends<OrderType>;
  summaryKey: string;
};

const customerBaseTotalsInsightToChartData = (
  insight: CustomerBaseTotalsInsight | undefined,
): ChartData<Legends<'total'>> => {
  if (!insight) {
    return [];
  }

  return insight.map(({ date, totalCustomersCount }) => ({
    total: totalCustomersCount,
    name: date ?? '',
  }));
};

const customersByCustomerTypeInsightToChartData = (
  insight: CustomersByCustomerTypeInsight | undefined,
): ChartData<Legends<CustomerType>> => {
  if (!insight) {
    return [];
  }

  return insight.map(({ date, totalCustomersCount, firstTimeCustomersCount, returningCustomersCount }) => ({
    [CustomerType.NEW]: firstTimeCustomersCount,
    [CustomerType.RETURNING]: returningCustomersCount,
    summaryValue: totalCustomersCount,
    name: date ?? '',
  }));
};

const customersByOrderTypeInsightToChartData = (
  insight: CustomersByOrderTypeInsight | undefined,
): ChartData<Legends<OrderType>> => {
  const dataByDates = insight?.data.customers.split;
  if (!dataByDates) {
    return [];
  }

  return dataByDates.map(({ label, split, total }) => {
    const regularCustomersPart = split?.find(part => part.label === OrderType.Regular);
    const woltPlusCustomersPart = split?.find(part => part.label === OrderType.WoltPlus);
    const totalCustomers = total?.totalCustomersCount ?? 0;

    return {
      [OrderType.Regular]: regularCustomersPart?.total?.totalCustomersCount ?? 0,
      [OrderType.WoltPlus]: woltPlusCustomersPart?.total?.totalCustomersCount ?? 0,
      total: totalCustomers,
      summaryValue: totalCustomers,
      name: label ?? '',
    };
  });
};

const CustomerBaseChart = ({
  insightEntity,
  dateInterval,
  selectedChartType,
  selectedLegends,
  summaryKey,
}: CustomerBaseChartProps) => {
  const customerBaseTotalsInsight = useCustomerBaseTotalsInsight(
    insightEntity,
    selectedChartType === 'TOTAL' ? dateInterval : null,
  );

  const customersByOrderTypeInsight = useCustomersByOrderTypeInsight(
    insightEntity,
    selectedChartType === 'REGULAR_VS_WOLT_PLUS' ? dateInterval : null,
  );

  const customersByCustomerTypeInsight = useCustomersByCustomerTypeInsight(
    insightEntity,
    selectedChartType === 'NEW_VS_RETURNING' ? dateInterval : null,
  );

  const { chartData, dataState } = useMemo(() => {
    if (selectedChartType === 'NEW_VS_RETURNING') {
      const { data, ...dataState } = customersByCustomerTypeInsight;
      return {
        chartData: customersByCustomerTypeInsightToChartData(data),
        dataState,
      };
    }

    if (selectedChartType === 'REGULAR_VS_WOLT_PLUS') {
      const { data, ...dataState } = customersByOrderTypeInsight;
      return {
        chartData: customersByOrderTypeInsightToChartData(data),
        dataState,
      };
    }

    const { data, ...dataState } = customerBaseTotalsInsight;
    return {
      chartData: customerBaseTotalsInsightToChartData(data),
      dataState,
    };
  }, [selectedChartType, customerBaseTotalsInsight, customersByCustomerTypeInsight, customersByOrderTypeInsight]);

  if (dataState.isError) {
    return <ErrorView onRetry={dataState.refetch} />;
  }

  if (dataState.isLoading || dataState.isFetching) {
    return <LoadingView />;
  }

  return (
    <VerticalBarChart
      legends={selectedLegends}
      axisIntegerOnly={{ [LegendChartType.BAR]: true }}
      data={chartData}
      summaryKey={summaryKey}
    />
  );
};

export default CustomerBaseChart;
